import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import {
    avvColumns,
    avvSorting,
    ledgerAccountColumns,
    serviceColumns,
    storageLocationColumns,
    vatColumns,
} from '@Constants/TableColumns';
import { buildPositionElementId } from '@Hooks/Navigation';
import {
    EnumFrontendLayoutSetting,
    EnumProcessState,
    EnumServiceType,
    EnumTaxationMode,
    getEnumUnitLabel,
    getPriceLabel,
    type ApiAvv,
    type ApiLedgerAccount,
    type ApiOrderPosition,
    type ApiService,
    type ApiStorageLocation,
    type ApiVat,
    type ApiWeighingSlip,
} from '@Model';
import { FrameEditPriceList } from '@Modules/Billing';
import { usePaymentMode, WeighingSlip } from '@Modules/Orders';
import {
    FrameEditAvv,
    FrameEditLedgerAccount,
    FrameEditService,
    FrameEditStorageLocation,
    FrameEditVat,
} from '@Modules/Stammdaten';
import {
    Badge,
    BadgeGroup,
    Button,
    Card,
    DateInput,
    defaultIconSize,
    DivWithSpacing,
    FlexRow,
    getSpacings,
    Grid,
    GridColumn,
    GridColumns,
    isLinked,
    type ISubFrameBodyProps,
    LabeledDiv,
    NumberInput,
    renderCurrency,
    TextInput,
    UnlabeledField,
    useEditSubObject,
    useGuiBinder,
    useLinkedFieldInFrame,
    useLinkedObjectOverlay,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconPlus } from '@tabler/icons-react';
import { forwardRef, useCallback, useMemo, useState } from 'react'; // forwardRef hinzugefügt
import { WeighingSlipBadge } from './WeighingSlipBadge';

function forceReadOnly(processState: EnumProcessState, name: string) {
    if (processState == EnumProcessState.Completed) {
        return name !== 'remarks';
    } else {
        return false;
    }
}

type OrderPositionProps = ISubFrameBodyProps<ApiOrderPosition> & {
    priceDisplay: EnumTaxationMode;
    processState: EnumProcessState;
};

export const OrderPosition = forwardRef<HTMLDivElement, OrderPositionProps>(function OrderPosition(props, ref) {
    const i18n = useTranslate('');

    const apiOrderPosition: ApiOrderPosition = props.data;
    const baseApiObjectPath = `${props.apiResourcePath}/${apiOrderPosition.id}`;

    const binder = useGuiBinder<ApiOrderPosition>({
        ...props,
        forceReadOnly: (name) => forceReadOnly(props.data.process_state, name),
    });
    const quantityProps = binder.bindToProp<number>('quantity');
    const [isWeighingSlipCardClosed, setIsWeighingSlipCardClosed] = useState(true);

    const linkStorageLocation = useLinkedFieldInFrame(props, 'storage_location', FrameEditStorageLocation);
    const showsStorageLocation =
        apiOrderPosition.service.type === EnumServiceType.Material || isLinked(apiOrderPosition.storage_location);
    const linkLedgerAccount = useLinkedFieldInFrame(props, 'ledger_account', FrameEditLedgerAccount);
    const linkService = useLinkedFieldInFrame(props, 'service', FrameEditService);
    const linkVat = useLinkedFieldInFrame(props, 'vat', FrameEditVat, false);
    const linkAvv = useLinkedFieldInFrame(props, 'avv', FrameEditAvv, false);

    const {
        addSubObject: addWeighingSlip,
        requestUpdateSubObject: requestUpdateWeighingSlip,
        deleteSubObjects: deleteWeighingSlip,
    } = useEditSubObject<ApiWeighingSlip, ApiOrderPosition>(
        baseApiObjectPath,
        'weighing_slips',
        props.requestUpdateState,
    );

    const { ensurePaymentMode } = usePaymentMode(apiOrderPosition.price_per_unit, props.requestUpdateState);

    const { onClick: clickLinkedPriceList } = useLinkedObjectOverlay({
        Renderer: FrameEditPriceList,
        canAdd: false,
    });

    const nonNullLinkedPriceListId = apiOrderPosition.price_list.foreign_key ?? '';
    const openPriceListInModal = useCallback(() => {
        clickLinkedPriceList(nonNullLinkedPriceListId);
    }, [clickLinkedPriceList, nonNullLinkedPriceListId]);

    const onAddWeighingSlip = useCallback(() => {
        setIsWeighingSlipCardClosed(false);
        return addWeighingSlip();
    }, [addWeighingSlip]);

    const weighingSlipBadges = useMemo(
        () =>
            apiOrderPosition.weighing_slips.map((ws) => (
                <WeighingSlipBadge
                    key={ws.id}
                    number={ws.number}
                    externalId={ws.external_id}
                    net={ws.net_weight}
                    unit={ws.unit}
                />
            )),
        [apiOrderPosition.weighing_slips],
    );

    return (
        <DivWithSpacing
            ref={ref}
            id={buildPositionElementId(props.data.id)}
            padding={getSpacings('sm')}
            className="w-4/5"
            name="Position"
        >
            <Grid columns={9}>
                <GridColumns spans={[3, 3, 3]}>
                    <LinkedFieldWithLayoutSettings<ApiService>
                        layoutSettingsContext={EnumFrontendLayoutSetting.ServicesTableLayout}
                        {...binder.bindToLinkProperty(linkService.linkApi)}
                        {...linkService.events}
                        columns={serviceColumns}
                        label={i18n('Leistung')}
                    />
                    {showsStorageLocation ? (
                        <LinkedFieldWithLayoutSettings<ApiStorageLocation>
                            layoutSettingsContext={EnumFrontendLayoutSetting.StorageLocationsTableLayout}
                            {...binder.bindToLinkProperty(linkStorageLocation.linkApi)}
                            {...linkStorageLocation.events}
                            columns={storageLocationColumns}
                            label={i18n('Lagerort')}
                        />
                    ) : (
                        <FlexRow />
                    )}
                    <DateInput {...binder.bindToProp('completed_at')} type="datetime" label={i18n('Erledigt am')} />
                </GridColumns>
                <GridColumn span={9}>
                    <TextInput {...binder.bindToProp('label')} label={i18n('Positionstext')} inputSize={2} />
                </GridColumn>
                <GridColumns spans={[3, 3, 3]}>
                    <FlexRow>
                        <NumberInput
                            {...quantityProps}
                            // Quantity is derived from the sum of the weighing slips' weights, so it must not be editable.
                            readOnly={quantityProps.readOnly || apiOrderPosition.weighing_slips.length > 0}
                            label={i18n('Menge')}
                            decimals={apiOrderPosition.service.decimal_places_amount}
                        />
                        <div className="flex-shrink">
                            <LabeledDiv htmlFor="unit" label="Einheit">
                                <Badge key="unit" variant="primary" type="input" size="sm">
                                    {getEnumUnitLabel(apiOrderPosition.service.unit)}
                                </Badge>
                            </LabeledDiv>
                        </div>
                    </FlexRow>
                    <FlexRow>
                        <NumberInput
                            {...binder.bindToProp('price_per_unit', ensurePaymentMode)}
                            decimals={apiOrderPosition.service.decimal_places_price}
                            label="Einzelpreis in €"
                        />
                        {apiOrderPosition.price_per_unit !== 0 && (
                            <div className="flex-shrink whitespace-nowrap" title={getPriceLabel(apiOrderPosition)}>
                                <UnlabeledField>
                                    {isLinked(apiOrderPosition.price_list) ? (
                                        <Badge key="invoice" variant="info" type="input" onClick={openPriceListInModal}>
                                            {getPriceLabel(apiOrderPosition)}
                                        </Badge>
                                    ) : (
                                        <Badge key="invoice" variant="info" type="input">
                                            {getPriceLabel(apiOrderPosition)}
                                        </Badge>
                                    )}
                                </UnlabeledField>
                            </div>
                        )}
                    </FlexRow>
                    {props.priceDisplay === EnumTaxationMode.PriceIsGross ? (
                        <LabeledDiv key="total_gross_price" htmlFor="" label="Gesamtpreis in € (Brutto)">
                            <Badge variant="primary" type="input" size="sm" label="Gesamtpreis in € (Brutto)">
                                {renderCurrency(
                                    apiOrderPosition.total_gross_price,
                                    apiOrderPosition.service.decimal_places_price,
                                )}
                            </Badge>
                        </LabeledDiv>
                    ) : (
                        <LabeledDiv key="total_net_price" htmlFor="" label="Gesamtpreis in € (Netto)">
                            <Badge variant="primary" type="input" size="sm" label="Gesamtpreis in € (Netto)">
                                {renderCurrency(
                                    apiOrderPosition.total_net_price,
                                    apiOrderPosition.service.decimal_places_price,
                                )}
                            </Badge>
                        </LabeledDiv>
                    )}
                </GridColumns>
                <GridColumns spans={[3, 3, 3]}>
                    <LinkedFieldWithLayoutSettings<ApiVat>
                        layoutSettingsContext={EnumFrontendLayoutSetting.VatsTableLayout}
                        {...binder.bindToLinkProperty(linkVat.linkApi)}
                        {...linkVat.events}
                        columns={vatColumns}
                        label={i18n('MwSt.')}
                    />
                    <LinkedFieldWithLayoutSettings<ApiLedgerAccount>
                        layoutSettingsContext={EnumFrontendLayoutSetting.LedgerAccountsTableLayout}
                        {...binder.bindToLinkProperty(linkLedgerAccount.linkApi)}
                        {...linkLedgerAccount.events}
                        columns={ledgerAccountColumns}
                        label={i18n('Sachkonto')}
                    />
                    <LinkedFieldWithLayoutSettings<ApiAvv>
                        layoutSettingsContext={EnumFrontendLayoutSetting.AvvsTableLayout}
                        {...binder.bindToLinkProperty(linkAvv.linkApi)}
                        {...linkAvv.events}
                        columns={avvColumns}
                        sorting={avvSorting}
                        label={i18n('Avv')}
                    />
                </GridColumns>
                <GridColumn span={9}>
                    <TextInput {...binder.bindToProp('remarks')} label={i18n('Bemerkung')} inputSize={2} />
                </GridColumn>
            </Grid>

            <Card
                name="weighing_slips"
                label={`Wiegescheine (${apiOrderPosition.weighing_slips.length})`}
                border="withoutBorder"
                additionalHeaderAlignment="left"
                padding={{ top: 'sm' }}
                key={apiOrderPosition.weighing_slips.length}
                closed={isWeighingSlipCardClosed}
                collapsable={!!apiOrderPosition.weighing_slips.length}
                fastTransition
                additionalHeaderOptions={
                    <FlexRow>
                        <Button
                            onClick={onAddWeighingSlip}
                            size="xxs"
                            appearance="outlined"
                            type="square"
                            locked={!props.canEdit}
                            label="Wiegeschein hinzufügen"
                        >
                            <IconPlus size={defaultIconSize} />
                        </Button>
                        <BadgeGroup>{weighingSlipBadges}</BadgeGroup>
                    </FlexRow>
                }
            >
                {apiOrderPosition.weighing_slips.map((weighingSlip) => (
                    <WeighingSlip
                        key={weighingSlip.id}
                        data={weighingSlip}
                        meta={undefined}
                        canEdit={props.canEdit}
                        apiResourcePath={`${baseApiObjectPath}/weighing_slips`}
                        requestUpdateState={(u) => requestUpdateWeighingSlip(u, weighingSlip.id)}
                        stateQueueDepth={props.stateQueueDepth}
                        delete={() => deleteWeighingSlip([weighingSlip.id])}
                        refetchData={props.refetchData}
                    />
                ))}
            </Card>
        </DivWithSpacing>
    );
});
